import React, {Fragment, useState} from 'react';
import {Icons, Edit, Breadcrumbs, List, Tabs, Card, DateContext, Activities, Flex, Icon, Layout, Calendar} from 'components';
import {noop, shorten, useList, filter, useMutationObserver, useNamedSelector, returnNull, overEvery} from 'helpers';
import {Activity, Moment, RouteComponentProps} from 'types';
import {activities} from 'store';
import css from './routines.module.scss';

export function RoutinesStats({match: {params}}: RouteComponentProps<{module: string}>) {
  const [q, setSearchQuery] = useState('');
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState(7);
  const [start, end] = useNamedSelector('calendar.range', {length, position});
  const isEditable = useNamedSelector('relations.isEditable');
  const isEligible = activity => activities.status(activity) === 'Not Started';

  const all = useNamedSelector('activities.list', {
    module: params.module,
    filter: overEvery(isEditable, isEligible)
  });

  const [list, Last] = useList(all, {
    search: { q, keys: ['name', 'note', 'tag'] },
    size: 5
  });

  return (
    <Fragment>
      <Layout.Header>
        <div className="max-container">
          <Breadcrumbs>
            Routines
            <b className="long">Completion Rate</b>
          </Breadcrumbs>
          <Tabs className="spacing">
            <Tabs.Tab onClick={() => setLength(7) || setPosition(0)} pressed={length === 7}>7 days</Tabs.Tab>
            <Tabs.Tab onClick={() => setLength(30) || setPosition(0)} pressed={length === 30}>30 days</Tabs.Tab>
            <Flex className="pull-right secondary">
              {start.range(end)}
              <Flex className={['small-gutter', 'black']}>
                <Icon onClick={() => setPosition(position - 1)} name="chevron-left" />
                <Icon onClick={() => setPosition(position + 1)} name="chevron-right" disabled={position === 0} />
              </Flex>
            </Flex>
          </Tabs>
          {all.length > 5 &&
            <p>
              <Edit
                value={q}
                setValue={setSearchQuery}
                placeholder="Search..."
                Prefix={Icons.Search}
              />
            </p>
          }
        </div>
      </Layout.Header>
      <List className="medium-gutter max-container">
        {list.map(activity => activity.id &&
          <Activities.Card
            // image={false}
            onClick={noop}
            ActionMenu={returnNull}
            Body={Body}
            end={end}
            Footer={returnNull}
            id={activity.id}
            key={activity.id}
            start={start}
          />
        )}
        <Last />
      </List>
    </Fragment>
  );
}

function Body({activity, start, end}) {
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const ref = useMutationObserver(() => {
    const total = ref.current?.querySelectorAll('.react-datepicker__day:not(.react-datepicker__day--outside-month, .react-datepicker__day--disabled) [role="checkbox"]');
    const eligible = filter(total, node => !/skipped|muted|disabled/i.test(node.dataset.status));
    setTotal(eligible.length);
    setCompleted(filter(eligible, node => node.getAttribute('aria-checked') === 'true').length);
  });
  return (
    <Card.Body>
      <Card.Text>{activity.name}</Card.Text>
      <Card.Note>{shorten(activity.note, { words: 30 })}</Card.Note>
      <div className={css.calendar} ref={ref}>
        <Calendar start={start} end={end} Day={Day} activity={activity} />
      </div>
      <Card.Progress now={completed} max={total}>
        <Flex>
          <Activities.Card.Repetition id={activity.id} />
          {total > 0 &&
            <span className="pull-right">{Math.round(100*completed/total)}%</span>
          }
        </Flex>
      </Card.Progress>
    </Card.Body>
  );
}

function Day({date, activity}: {date: Moment, activity: Activity}) {
  const {type} = useNamedSelector('activities.details', {id: activity.id, context: 'activities'});
  return (
    <DateContext.Provider value={date}>
      <div className={css.day}>
        <Activities.Checkbox activity={activity} type={type} retroperspective />
      </div>
    </DateContext.Provider>
  );
}
